/**
 * Profile Completion Tips Component
 * Shows profile completion progress and checklist items
 */

import { Check } from "lucide-react"
import { useMemo } from "react"
import type { User } from "@/features/auth/store/authStore"
import { useTranslation } from "@/language"

interface ProfileCompletionTipsProps {
  user: User
  // Form state props for reactive completion tracking (multi-select arrays)
  selectedTraditions?: Array<{ id: string; title: string; is_custom?: boolean }>
  selectedBackgrounds?: Array<{ id: string; title: string; is_custom?: boolean }>
  otherTraditions?: string[]
  otherBackgrounds?: string[]
  selectedIntentions?: string[]
  profilePhotos?: Array<{ id: string; src: string; isCover?: boolean }>
  // Basic info form state for reactive completion tracking
  formName?: string | null
  formUsername?: string | null
  formBio?: string | null
  formCity?: string | null
}

interface CompletionItem {
  key: string
  label: string
  completed: boolean
}

export function ProfileCompletionTips({
  user,
  selectedTraditions,
  selectedBackgrounds,
  otherTraditions,
  otherBackgrounds,
  selectedIntentions,
  profilePhotos,
  formName,
  formUsername,
  formBio,
  formCity,
}: ProfileCompletionTipsProps) {
  const { t } = useTranslation("profile")

  const completionItems = useMemo<CompletionItem[]>(() => {
    // Check for cover image - combine saved photos and new photos from form
    const savedCoverPhoto = user.profile_photos?.some(p => p.isCover || p.is_cover_image) ?? false
    const newCoverPhoto = profilePhotos?.some(p => p.isCover) ?? false
    const hasCoverImage = savedCoverPhoto || newCoverPhoto

    // Check for spiritual traditions - ONLY use current form state (multi-select)
    const hasSpiritualTraditions =
      (selectedTraditions?.length ?? 0) > 0 ||
      (otherTraditions?.some(t => t && t.trim().length > 0) ?? false)

    // Check for ethnic backgrounds - ONLY use current form state (multi-select)
    const hasEthnicBackgrounds =
      (selectedBackgrounds?.length ?? 0) > 0 ||
      (otherBackgrounds?.some(b => b && b.trim().length > 0) ?? false)

    // Check for intentions - ONLY use current form state
    const hasIntentions = (selectedIntentions?.length ?? 0) > 0

    // Check for basic info (name, username, bio, city) - ONLY use current form state
    const hasBasicInfo = !!(formName && formUsername && formBio && formCity)

    return [
      {
        key: "basicInfo",
        label: t("profileCompletion.basicInfo"),
        completed: hasBasicInfo,
      },
      {
        key: "coverImage",
        label: t("profileCompletion.coverImage"),
        completed: hasCoverImage,
      },
      {
        key: "spiritualTraditions",
        label: t("profileCompletion.spiritualTraditions"),
        completed: hasSpiritualTraditions,
      },
      {
        key: "ethnicBackgrounds",
        label: t("profileCompletion.ethnicBackgrounds"),
        completed: hasEthnicBackgrounds,
      },
      {
        key: "intentions",
        label: t("profileCompletion.intentions"),
        completed: hasIntentions,
      },
    ]
  }, [
    user,
    selectedTraditions,
    selectedBackgrounds,
    otherTraditions,
    otherBackgrounds,
    selectedIntentions,
    profilePhotos,
    formName,
    formUsername,
    formBio,
    formCity,
    t,
  ])

  const completedCount = completionItems.filter(item => item.completed).length
  const totalItems = completionItems.length
  const progressPercentage = Math.round((completedCount / totalItems) * 100)

  return (
    <div className="hidden md:block bg-card rounded-xl py-4 px-5">
      <h3 className="text-[14px] leading-[22px] font-semibold text-charcoal mb-3">
        {t("profileCompletion.completeProfile", { percentage: progressPercentage })}
      </h3>

      {/* Progress Bar */}
      <div className="w-full h-4 bg-brand-white-alpha-10 rounded-full mb-4 overflow-hidden">
        <div
          className="h-full bg-brand-olive transition-all duration-300 ease-in-out rounded-full"
          style={{ width: `${progressPercentage}%` }}
        />
      </div>

      {/* Checklist Items */}
      <ul className="space-y-2 mt-6">
        {completionItems.map(item => (
          <li
            key={item.key}
            className={`flex items-center justify-between gap-3 text-black dark-text-white my-4.5 last:mb-0`}
          >
            {/* <Checkbox checked={item.completed} disabled /> */}
            <span className={item.completed ? "line-through text-sm" : "text-sm font-semibold"}>
              {item.label}
            </span>

            {item.completed ? (
              <div className="rounded-full bg-brand-olive p-1">
                <Check className="w-4 h-4 text-brand-olive text-white" />
              </div>
            ) : (
              <div className="text-[14px] text-black bg-text-white">20%</div>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
